import { useMutation, useQueryClient } from "react-query";

import { grantDocumentOverride, revokeDocumentOverride } from "api/documentGenerationChecklist";

interface UseDocumentOverrideOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface UseDocumentOverrideReturn {
  grantOverride: (documentType: string) => void;
  revokeOverride: () => void;
  isSaving: boolean;
}

/**
 * Grants or withdraws the client's document generation override.
 * Both calls are idempotent server-side, so a double submit is safe.
 * @param clientId - The client the override belongs to.
 * @param options - Optional callbacks: onSuccess, onError.
 */
const useDocumentOverride = (
  clientId: string | number,
  options: UseDocumentOverrideOptions = {}
): UseDocumentOverrideReturn => {
  const queryClient = useQueryClient();

  const mutationOptions = {
    onSuccess: () => {
      // Prefix match — the status key also carries the clinician id.
      queryClient.invalidateQueries(["documentChecklistStatus", clientId]);
      options.onSuccess?.();
    },
    onError: (error: unknown) => {
      options.onError?.(error);
    },
  };

  const { mutate: grantOverride, isLoading: isGranting } = useMutation(
    (documentType: string) => grantDocumentOverride(clientId, documentType),
    mutationOptions
  );

  const { mutate: revokeOverride, isLoading: isRevoking } = useMutation(
    () => revokeDocumentOverride(clientId),
    mutationOptions
  );

  return { grantOverride, revokeOverride, isSaving: isGranting || isRevoking };
};

export default useDocumentOverride;
